import {
  FocusEventHandler,
  forwardRef,
  ReactNode,
  useId,
  useMemo,
  useState,
} from 'react';
import { Key, Select, SelectItem, Tag, TagGroup } from '@backstage/ui';
import { RiAddLine } from '@remixicon/react';
// match-sorter keeps the typo tolerance of the former MUI autocomplete:
// https://v5.mui.com/material-ui/react-autocomplete/#advanced
import { matchSorter, rankings } from 'match-sorter';

import { FieldHeader, FieldMessage, joinIds } from '../Field/Field';
import styles from './Autocomplete.module.css';

export type AutocompleteOption = {
  label: string;
  value: string;
};

/** Options grouped under a section title, keyed by the title. */
export type AutocompleteGroupedOptions = Record<string, AutocompleteOption[]>;

const CREATE_NEW_ID = '__create_new__';
const NO_OPTIONS_ID = '__no_options__';

interface AutocompleteFieldBaseProps {
  name: string;
  id?: string;
  label?: string;
  /** Error message below the field; a non-empty value marks it invalid. */
  helperText?: ReactNode;
  /** Neutral help text below the field, replaced by `helperText`. */
  description?: ReactNode;
  /** Marks the field invalid without a message. */
  error?: boolean | null;
  /** Options when `isGroupSelect` is not set. */
  selectOption?: AutocompleteOption[];
  placeholder?: string;
  /** Text of the info button next to the label. */
  tooltip?: ReactNode;
  /** Element at the right end of the label row. */
  actionItem?: ReactNode;
  /** Uses `groupSelectOptions` and shows a section per group. */
  isGroupSelect?: boolean;
  groupSelectOptions?: AutocompleteGroupedOptions;
  onBlur?: FocusEventHandler<Element>;
  disabled?: boolean;
  required?: boolean;
  /** Shows a red asterisk after the label. */
  requiredMark?: boolean;
  /** Hides the chevron of the trigger. */
  showDropdownIcon?: boolean;
  /** Shown in the list when the search matches no option. */
  noOptionText?: ReactNode;
  /** Turns off the search field; the list then shows all options. */
  disableSearch?: boolean;
  /** Placeholder of the search field in the list. */
  searchPlaceholder?: string;
  /**
   * Custom content of an option (flat options only). Gets the option object
   * from `selectOption`, including extra fields.
   */
  renderCustomOption?(option: AutocompleteOption): ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  /** Offers a "create" option when the search matches no option. */
  showCreateNewOption?: boolean;
  /** Called with the search text when the "create" option is chosen. */
  onCreateNew?: (searchValue: string) => void;
  /** Text of the "create" option. */
  createNewLabel?: string;
  size?: 'small' | 'medium';
  className?: string;
  /** @deprecated The trigger always shows the label of the selected option. */
  showSelectedLabel?: boolean;
  'aria-label'?: string;
}

type AutocompleteFieldSingleProps = {
  multiple?: false;
  /** The selected value; `''` or `null` when none. */
  value?: string | null;
  /** Called with the selected value. */
  onChange?(value: string): void;
};

type AutocompleteFieldMultipleProps = {
  /** Allows selecting several options, shown as removable tags. */
  multiple: true;
  /** The selected values. */
  value?: string[] | null;
  /** Called with all selected values. */
  onChange?(values: string[]): void;
};

export type AutocompleteFieldProps = AutocompleteFieldBaseProps &
  (AutocompleteFieldSingleProps | AutocompleteFieldMultipleProps);

type ListItem = {
  id: string;
  option?: AutocompleteOption;
};

const matchesQuery = (option: AutocompleteOption, query: string) =>
  matchSorter([option], query, {
    keys: ['label', 'value'],
    threshold: rankings.MATCHES,
  }).length > 0;

const toStrings = (keys: Iterable<Key>) => Array.from(keys, String);

/**
 * Searchable select of the Xelerate forms on the Backstage UI `Select`:
 * typo-tolerant search, sections, multiple selection with removable tags,
 * custom option content and an optional "create" option.
 */
export const AutocompleteField = forwardRef<
  HTMLDivElement,
  AutocompleteFieldProps
>((props, ref) => {
  const {
    name,
    id,
    label,
    helperText,
    description,
    error = false,
    selectOption = [],
    placeholder,
    tooltip,
    actionItem = null,
    multiple = false,
    isGroupSelect = false,
    groupSelectOptions = {},
    value,
    onChange,
    onBlur,
    disabled,
    required,
    requiredMark = false,
    showDropdownIcon = true,
    noOptionText,
    disableSearch = false,
    searchPlaceholder = 'Search...',
    renderCustomOption,
    onOpen,
    onClose,
    showCreateNewOption = false,
    onCreateNew,
    createNewLabel = 'Create New Group',
    size = 'medium',
    className,
    'aria-label': ariaLabel,
  } = props;

  const [query, setQuery] = useState('');
  const emit = onChange as ((next: string | string[]) => void) | undefined;

  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const labelId = `${fieldId}-label`;
  const messageId = `${fieldId}-message`;
  const isInvalid = error || !!helperText;
  const message = helperText || description;

  const allOptions = useMemo(
    () =>
      isGroupSelect ? Object.values(groupSelectOptions).flat() : selectOption,
    [isGroupSelect, groupSelectOptions, selectOption],
  );
  const optionsById = useMemo(
    () => new Map(allOptions.map(option => [option.value, option])),
    [allOptions],
  );

  const search = query.trim();
  const hasMatch =
    search === '' || allOptions.some(option => matchesQuery(option, search));
  const showCreateNew = showCreateNewOption && !hasMatch;
  const showNoOptionText = !!noOptionText && !hasMatch && !showCreateNew;

  const selectedValues: string[] = multiple
    ? (Array.isArray(value) ? value : []).filter(Boolean)
    : [];
  const selectedOptions = selectedValues
    .map(selected => optionsById.get(selected))
    .filter((option): option is AutocompleteOption => !!option);

  const filter = (item: { id: Key }, inputValue: string) => {
    if (item.id === CREATE_NEW_ID || item.id === NO_OPTIONS_ID) {
      return true;
    }
    const option = optionsById.get(String(item.id));
    return !!option && matchesQuery(option, inputValue.trim());
  };

  const searchProps = disableSearch
    ? undefined
    : {
        placeholder: searchPlaceholder,
        inputValue: query,
        onInputChange: setQuery,
        filter,
      };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      onOpen?.();
    } else {
      setQuery('');
      onClose?.();
    }
  };

  const handleSingleChange = (key: Key | null) => {
    if (key === CREATE_NEW_ID) {
      onCreateNew?.(search);
      return;
    }
    emit?.(key === null ? '' : String(key));
  };

  const handleMultipleChange = (keys: Key[]) => {
    if (keys.includes(CREATE_NEW_ID)) {
      onCreateNew?.(search);
    }
    emit?.(
      toStrings(keys).filter(
        key => key !== CREATE_NEW_ID && key !== NO_OPTIONS_ID,
      ),
    );
  };

  const handleRemove = (keys: Set<Key>) => {
    emit?.(selectedValues.filter(selected => !keys.has(selected)));
  };

  // Plain options go through `options`, which renders sections and the check
  // indicator. Custom option content and the "no options" text need `items`.
  const toOption = (option: AutocompleteOption) => ({
    id: option.value,
    label: option.label,
  });
  const createNewOption = {
    id: CREATE_NEW_ID,
    label: createNewLabel,
    leadingIcon: <RiAddLine aria-hidden="true" />,
  };
  const options = [
    ...(isGroupSelect
      ? Object.entries(groupSelectOptions).map(([title, groupOptions]) => ({
          title,
          options: groupOptions.map(toOption),
        }))
      : selectOption.map(toOption)),
    ...(showCreateNew ? [createNewOption] : []),
  ];

  const rendersItems = showNoOptionText || !!renderCustomOption;
  const items: ListItem[] = showNoOptionText
    ? [{ id: NO_OPTIONS_ID }]
    : allOptions.map(option => ({ id: option.value, option }));

  const renderItem = (item: ListItem) => {
    if (!item.option) {
      return (
        <SelectItem
          id={item.id}
          textValue="No options"
          isDisabled
          className={styles.noOptions}
        >
          {noOptionText}
        </SelectItem>
      );
    }
    return (
      <SelectItem
        id={item.id}
        textValue={item.option.label}
        className={styles.customOption}
      >
        {renderCustomOption
          ? renderCustomOption(item.option)
          : item.option.label}
      </SelectItem>
    );
  };

  const labelProps = label
    ? { 'aria-labelledby': labelId }
    : { 'aria-label': ariaLabel ?? placeholder ?? name };

  const sharedProps = {
    ...labelProps,
    ref,
    id: fieldId,
    name,
    placeholder: placeholder || undefined,
    isDisabled: disabled,
    isRequired: required,
    size,
    onBlur,
    onOpenChange: handleOpenChange,
    'aria-describedby': joinIds(!!message && messageId),
  };

  const singleValue = typeof value === 'string' && value !== '' ? value : null;

  let select: ReactNode;
  if (rendersItems && multiple) {
    select = (
      <Select
        {...sharedProps}
        selectionMode="multiple"
        value={selectedValues}
        onChange={handleMultipleChange}
        items={items}
        search={searchProps}
      >
        {renderItem}
      </Select>
    );
  } else if (rendersItems) {
    select = (
      <Select
        {...sharedProps}
        selectionMode="single"
        value={singleValue}
        onChange={handleSingleChange}
        items={items}
        search={searchProps}
      >
        {renderItem}
      </Select>
    );
  } else if (multiple) {
    select = (
      <Select
        {...sharedProps}
        selectionMode="multiple"
        value={selectedValues}
        onChange={handleMultipleChange}
        options={options}
        search={searchProps}
      />
    );
  } else {
    select = (
      <Select
        {...sharedProps}
        selectionMode="single"
        value={singleValue}
        onChange={handleSingleChange}
        options={options}
        search={searchProps}
      />
    );
  }

  return (
    <div
      className={[styles.root, 'xlrt-AutocompleteField', className]
        .filter(Boolean)
        .join(' ')}
      data-invalid={isInvalid || undefined}
      data-hide-chevron={!showDropdownIcon || undefined}
    >
      <FieldHeader
        label={label}
        labelId={labelId}
        tooltip={tooltip}
        requiredMark={requiredMark}
        actionItem={actionItem}
      />
      {select}
      {multiple && selectedOptions.length > 0 && (
        <TagGroup
          aria-label={`Selected ${label || placeholder || name}`}
          onRemove={disabled ? undefined : handleRemove}
          className={`${styles.tags} xlrt-AutocompleteFieldTags`}
        >
          {selectedOptions.map(option => (
            <Tag key={option.value} id={option.value} size="medium">
              {option.label}
            </Tag>
          ))}
        </TagGroup>
      )}
      <FieldMessage id={messageId} invalid={!!helperText}>
        {message}
      </FieldMessage>
    </div>
  );
});

AutocompleteField.displayName = 'AutocompleteField';
